import React, { forwardRef, useImperativeHandle } from 'react';
import { DetectReceiptFraudRequest } from '../types';
import { features } from '../config';

const DEFAULT_CUSTOM_INSTRUCTIONS = `# Important Detection Tips

1. Follow the below chain of process to detect the receipt fraud:
- Step I. Locate the bounding box of the receipt to learn global visual characteristics.
- Step II. Locate the bounding box of each important data field to learn local visual characteristics.
- Step III. Based on global and local visual characteristics, call relevant tools to gain more signals as detection evidence.
- Step IV. Combine the **tool results** and **visual details** to detect the receipt fraud.`;


const DEFAULT_EXTRACTED_DATA = {
  "total_amount": "123.0",
  "invoice_date": "2025/12/31",
  "invoice_time": "23:59",
  "invoice_number": "12345",
  "product_line_items": []
};

export interface FormDataWithAuth extends DetectReceiptFraudRequest {
  auth_token: string;
  chat_complete_request_args_enabled?: boolean;
  custom_instructions_enabled?: boolean;
}

interface DetectReceiptFraudRequestFormProps {
  formData: FormDataWithAuth;
  onFormDataChange: (formData: FormDataWithAuth) => void;
  onSubmit: (request: DetectReceiptFraudRequest, authToken: string) => void;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

export interface DetectReceiptFraudRequestFormRef {
  isValid: () => boolean;
}

export const DetectReceiptFraudRequestForm = forwardRef<DetectReceiptFraudRequestFormRef, DetectReceiptFraudRequestFormProps>(({ 
  formData,
  onFormDataChange,
  onSubmit,
  disabled,
  onValidationChange
}, ref) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { auth_token, ...requestData } = formData;
    // Only include args in the request when enabled; otherwise send null
    const payload = {
      ...requestData,
      chat_complete_request_args: chatArgsEnabled ? requestData.chat_complete_request_args : null,
      custom_instructions: customInstructionsEnabled ? requestData.custom_instructions : null,
    };
    onSubmit(payload, auth_token);
  };

  const updateFormData = (updates: Partial<FormDataWithAuth>) => {
    onFormDataChange({ ...formData, ...updates });
  };

  // Custom Chat Completion Args (JSON) state
  const [chatArgsEnabled, setChatArgsEnabled] = React.useState(
    formData.chat_complete_request_args_enabled ?? !!formData.chat_complete_request_args
  );
  const [chatArgsText, setChatArgsText] = React.useState(
    formData.chat_complete_request_args ? JSON.stringify(formData.chat_complete_request_args, null, 2) : ''
  );
  const [chatArgsError, setChatArgsError] = React.useState<string | null>(null);

  const handleChatArgsChange = (text: string) => {
    setChatArgsText(text);
    if (text.trim() === '') {
      setChatArgsError(null);
      // Keep cache empty if user clears it explicitly
      updateFormData({ 
        chat_complete_request_args: null,
        chat_complete_request_args_enabled: chatArgsEnabled
      });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setChatArgsError(null);
        updateFormData({ 
          chat_complete_request_args: parsed,
          chat_complete_request_args_enabled: chatArgsEnabled
        });
      } else {
        setChatArgsError('JSON must be an object (e.g., { "temperature": 0.2 })');
      }
    } catch {
      setChatArgsError('Invalid JSON');
    }
  };

  const handleChatArgsToggle = () => {
    const newEnabled = !chatArgsEnabled;
    setChatArgsEnabled(newEnabled);
    if (!newEnabled) {
      // Disable: keep the text/cache but send null
      updateFormData({ 
        chat_complete_request_args: null,
        chat_complete_request_args_enabled: false
      });
    } else {
      // Enable: restore from cache if valid
      if (chatArgsText.trim() !== '') {
        try {
          const parsed = JSON.parse(chatArgsText);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            updateFormData({ 
              chat_complete_request_args: parsed,
              chat_complete_request_args_enabled: true
            });
            setChatArgsError(null);
          }
        } catch {
          // Keep as is if invalid
          updateFormData({ chat_complete_request_args_enabled: true });
        }
      } else {
        updateFormData({ chat_complete_request_args_enabled: true });
      }
    }
  };

  // Extracted Data (JSON) state
  const [extractedDataText, setExtractedDataText] = React.useState(
    () => JSON.stringify(formData.extracted_data, null, 2),
  );
  const [extractedDataError, setExtractedDataError] = React.useState<string | null>(null);

  const handleExtractedDataChange = (text: string) => {
    setExtractedDataText(text);
    if (text.trim() === '') {
      setExtractedDataError(null);
      updateFormData({ extracted_data: {} });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setExtractedDataError(null);
        updateFormData({ extracted_data: parsed });
      } else {
        setExtractedDataError('JSON must be an object');
      }
    } catch {
      setExtractedDataError('Invalid JSON');
    }
  };

  const handleLoadExtractedDataTemplate = (template: 'default') => {
    if (template === 'default') {
      setExtractedDataText(JSON.stringify(DEFAULT_EXTRACTED_DATA, null, 2));
      updateFormData({ extracted_data: DEFAULT_EXTRACTED_DATA });
    }
  };

  // Custom Instructions state
  const [customInstructionsEnabled, setCustomInstructionsEnabled] = React.useState(
    formData.custom_instructions_enabled ?? true
  );
  const [customInstructionsText, setCustomInstructionsText] = React.useState(
    formData.custom_instructions || ''
  );

  const handleCustomInstructionsChange = (text: string) => {
    setCustomInstructionsText(text);
    updateFormData({ 
      custom_instructions: text,
      custom_instructions_enabled: customInstructionsEnabled
    });
  };

  const handleCustomInstructionsToggle = () => {
    const newEnabled = !customInstructionsEnabled;
    setCustomInstructionsEnabled(newEnabled);
    if (!newEnabled) {
      // Disable: keep the text/cache but send null
      updateFormData({ 
        custom_instructions: null,
        custom_instructions_enabled: false
      });
    } else {
      // Enable: restore from cache
      updateFormData({ 
        custom_instructions: customInstructionsText,
        custom_instructions_enabled: true
      });
    }
  };

  const handleLoadCustomInstructionsTemplate = (template: 'default') => {
    if (template === 'default') {
      setCustomInstructionsText(DEFAULT_CUSTOM_INSTRUCTIONS);
      updateFormData({ 
        custom_instructions: DEFAULT_CUSTOM_INSTRUCTIONS,
        custom_instructions_enabled: customInstructionsEnabled
      });
    }
  };

  // Validation
  const isValid = React.useMemo(() => {
    return !extractedDataError && (!chatArgsEnabled || !chatArgsError);
  }, [extractedDataError, chatArgsEnabled, chatArgsError]);

  // Expose isValid via ref
  useImperativeHandle(ref, () => ({
    isValid: () => isValid,
  }));

  // Notify parent of validation changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  return (
    <form onSubmit={handleSubmit} className="request-form">
      {/* LLM Configuration */}
      <div className="form-section">
        <h3>LLM Configuration</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="llm-provider">LLM Provider *</label>
            <select
              id="llm-provider"
              value={formData.llm_model_provider}
              onChange={(e) => updateFormData({ llm_model_provider: e.target.value as 'openrouter' | 'google' })}
              disabled={disabled}
              required
            >
              <option value="openrouter">OpenRouter</option>
              <option value="google">Google</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="llm-model">Model Name</label>
            <input
              id="llm-model"
              type="text"
              value={formData.llm_model_name || ''}
              onChange={(e) => updateFormData({ llm_model_name: e.target.value || null })}
              placeholder="e.g., google/gemini-2.0-flash-exp:free"
              disabled={disabled}
            />
            <div className="form-help-text">
              <small>Leave empty to use provider's default model</small>
            </div>
          </div>
        </div>

        {formData.llm_model_provider === 'openrouter' && (
          <div className="form-group">
            <label htmlFor="openrouter-key">OpenRouter API Key</label>
            <input
              id="openrouter-key"
              type="password"
              value={formData.openrouter_api_key || ''}
              onChange={(e) => updateFormData({ openrouter_api_key: e.target.value || null })}
              placeholder="sk-or-..."
              disabled={disabled}
            />
            <div className="form-help-text">
              <small>Optional: Only required if not configured on server</small>
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="max-tool-calls">Max Allowed Tool Calls</label>
          <input
            id="max-tool-calls"
            type="number"
            min={0}
            value={formData.max_allowed_tool_calls ?? ''}
            onChange={(e) => updateFormData({ max_allowed_tool_calls: e.target.value !== '' ? parseInt(e.target.value, 10) : null })}
            placeholder="40"
            disabled={disabled}
          />
          <div className="form-help-text">
            <small>Maximum number of tool calls the agent can make. Leave empty to use server default</small>
          </div>
        </div>

        {features.enableAdvancedOptions && (
          <div className="form-group">
            <div className="toggle-label">
              <label htmlFor="chat-args-toggle">Custom Chat Completion Arguments</label>
              <label className="toggle-switch">
                <input
                  id="chat-args-toggle"
                  type="checkbox"
                  className="toggle-input"
                  checked={chatArgsEnabled}
                  onChange={handleChatArgsToggle}
                  disabled={disabled}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            {chatArgsEnabled && (
              <>
                <textarea
                  value={chatArgsText}
                  onChange={(e) => handleChatArgsChange(e.target.value)}
                  placeholder='{ "temperature": 0.2, "max_tokens": 1000 }'
                  rows={4}
                  disabled={disabled}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                {chatArgsError && (
                  <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    {chatArgsError}
                  </div>
                )}
                <div className="form-help-text">
                  <small>Additional arguments to pass to the LLM chat completion API (JSON object)</small>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Extracted Data */}
      <div className="form-section">
        <h3>Extracted Receipt Data</h3>
        
        <div className="form-group">
          <label htmlFor="extracted-data">Receipt Fields (JSON)</label>
          <div className="form-help-text" style={{ marginBottom: '0.5rem' }}>
                <small>Load template:</small>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleLoadExtractedDataTemplate('default')}
                    disabled={disabled}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    Default
                  </button>
                </div>
              </div>
          <textarea
            id="extracted-data"
            value={extractedDataText}
            onChange={(e) => handleExtractedDataChange(e.target.value)}
            placeholder='{ "merchant_name": "ABC Store", "total": "123.45", "date": "2024-01-15" }'
            rows={8}
            disabled={disabled}
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
          {extractedDataError && (
            <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              {extractedDataError}
            </div>
          )}
          <div className="form-help-text">
            <small>
              Provide extracted receipt data as a JSON object. Include fields like merchant_name, total, date, items, etc.
              This data will be analyzed alongside the receipt image for fraud detection.
            </small>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="form-section">
        <h3>Authentication</h3>
        
        <div className="form-group">
          <label htmlFor="auth-token">Authorization Token</label>
          <input
            id="auth-token"
            type="password"
            value={formData.auth_token}
            onChange={(e) => updateFormData({ auth_token: e.target.value })}
            placeholder="Bearer token (if required by server)"
            disabled={disabled}
          />
          <div className="form-help-text">
            <small>Leave empty if server doesn't require authentication</small>
          </div>
        </div>
      </div>

      {/* Custom Instructions */}
      <div className="form-section">
        <h3>Custom Instructions</h3>
        
        <div className="form-group">
          <div className="toggle-label">
            <label htmlFor="custom-instructions-toggle">Enable Custom Instructions</label>
            <label className="toggle-switch">
              <input
                id="custom-instructions-toggle"
                type="checkbox"
                className="toggle-input"
                checked={customInstructionsEnabled}
                onChange={handleCustomInstructionsToggle}
                disabled={disabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          {customInstructionsEnabled && (
            <>
              <div className="form-help-text" style={{ marginBottom: '0.5rem' }}>
                <small>Load template:</small>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleLoadCustomInstructionsTemplate('default')}
                    disabled={disabled}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    Default
                  </button>
                </div>
              </div>
              <textarea
                value={customInstructionsText}
                onChange={(e) => handleCustomInstructionsChange(e.target.value)}
                placeholder="Enter custom instructions for receipt fraud detection..."
                rows={10}
                disabled={disabled}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
              <div className="form-help-text">
                <small>Provide custom instructions to guide the fraud detection process</small>
              </div>
            </>
          )}
        </div>
      </div>
    </form>
  );
});

DetectReceiptFraudRequestForm.displayName = 'DetectReceiptFraudRequestForm';
